import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AdminModal, SkeletonLoader, StatusBadge } from "../../components/admin";
import AdminLayout from "../../components/layout/AdminLayout";
import { useToast } from "../../contexts/ToastContext";
import { API_BASE_URL } from "../../services/api/config";

interface ApplicationDetails {
  id: string;
  applicationNumber: string;
  status: string;
  givenNames: string | null;
  familyName: string | null;
  gender: string | null;
  dateOfBirth: string | null;
  nationality: string | null;
  placeOfBirth: string | null;
  email: string | null;
  whatsappNumber: string | null;
  passportNumber: string | null;
  passportExpiry: string | null;
  personalStatement: string | null;
  academicHistory: string | null;
  workExperience: string | null;
  references: string | null;
  paymentStatus: string;
  paymentReference: string | null;
  paymentIntentId: string | null;
  paidAt: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdAt: string;
  course: {
    title: string;
    university: {
      name: string;
    };
  };
  user: {
    email: string;
    firstName: string;
    lastName: string;
  };
  documents: Array<{
    id: string;
    name: string;
    documentType: string;
    isVerified: boolean;
    rejectionReason?: string | null;
    uploadedAt: string;
    url: string | null;
  }>;
}

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-shadow";

const AdminApplicationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { showSuccess, showError: showToastError } = useToast();
  const [application, setApplication] = useState<ApplicationDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");
  const [expandedContent, setExpandedContent] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<ApplicationDetails["documents"][0] | null>(null);
  const [showDocModal, setShowDocModal] = useState(false);
  const [docAction, setDocAction] = useState<"reject" | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [docActionLoading, setDocActionLoading] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    status: "",
    reviewNotes: "",
    sendEmail: true,
    emailMessage: "",
  });

  useEffect(() => {
    loadApplication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadApplication = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(`${API_BASE_URL}/api/admin/applications/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setApplication(response.data.data);
        setStatusUpdate({
          status: response.data.data.status,
          reviewNotes: response.data.data.reviewNotes || "",
          sendEmail: true,
          emailMessage: "",
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load application details");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!id || !statusUpdate.status) return;
    setUpdating(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_BASE_URL}/api/admin/applications/${id}/status`,
        statusUpdate,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess("Application status updated successfully!");
      loadApplication();
    } catch (err: any) {
      showToastError(err.response?.data?.message || "Failed to update application status");
    } finally {
      setUpdating(false);
    }
  };

  const handleSendEmail = async () => {
    if (!id) return;
    setSendingEmail(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_BASE_URL}/api/admin/applications/${id}/send-status-email`,
        { message: emailMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess("Email notification sent successfully!");
      setShowEmailModal(false);
      setEmailMessage("");
    } catch (err: any) {
      showToastError(err.response?.data?.message || "Failed to send email notification");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDocumentAction = async (docId: string, isVerified: boolean, reason?: string) => {
    if (!selectedDoc) return;
    setDocActionLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_BASE_URL}/api/admin/documents/${docId}/verify`,
        { isVerified, rejectionReason: reason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showSuccess(`Document ${isVerified ? "verified" : "rejected"} successfully!`);
      const updatedDoc = { ...selectedDoc, isVerified, rejectionReason: isVerified ? null : (reason || null) };
      setSelectedDoc(updatedDoc);
      setApplication((prev) =>
        prev
          ? { ...prev, documents: prev.documents.map((d) => (d.id === docId ? updatedDoc : d)) }
          : null
      );
      setDocAction(null);
      setRejectionReason("");
    } catch (err: any) {
      showToastError(err.response?.data?.message || "Failed to update document");
    } finally {
      setDocActionLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getContentCompleteness = () => {
    if (!application) return { filled: 0, total: 4 };
    const sections = [
      application.personalStatement,
      application.academicHistory,
      application.workExperience,
      application.references,
    ];
    const filled = sections.filter((s) => s && s.trim().length > 0).length;
    return { filled, total: 4 };
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-5">
          <div className="h-6 w-40 bg-gray-200 rounded animate-pulse" />
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
          <SkeletonLoader variant="detail-card" count={3} />
        </div>
      </AdminLayout>
    );
  }

  if (error || !application) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <p className="text-sm text-red-700 mb-4">{error || "Application not found"}</p>
          <Link
            to="/admin/applications"
            className="text-sm font-medium text-cyan-600 hover:text-cyan-800 transition-colors"
          >
            &larr; Back to Applications
          </Link>
        </div>
      </AdminLayout>
    );
  }

  const contentCompleteness = getContentCompleteness();

  const InfoField = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <div>
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value || <span className="text-gray-300">--</span>}</p>
    </div>
  );

  const timelineItems = [
    { label: "Created", date: application.createdAt, color: "bg-gray-400" },
    application.submittedAt && { label: "Submitted", date: application.submittedAt, color: "bg-blue-500" },
    application.paidAt && { label: "Paid", date: application.paidAt, color: "bg-emerald-500" },
    application.reviewedAt && { label: "Reviewed", date: application.reviewedAt, color: "bg-purple-500" },
  ].filter(Boolean) as { label: string; date: string; color: string }[];

  return (
    <AdminLayout>
      <div className="space-y-5">
        {/* Back + Header */}
        <div>
          <Link
            to="/admin/applications"
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-cyan-600 transition-colors mb-3"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Applications
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                #{application.applicationNumber}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {application.givenNames} {application.familyName}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <StatusBadge status={application.status} type="application" size="md" />
              <button
                onClick={() => setShowEmailModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email
              </button>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-6 py-4">
          <div className="flex flex-wrap items-center gap-6">
            {timelineItems.map((item, i) => (
              <React.Fragment key={item.label}>
                {i > 0 && <div className="hidden sm:block w-8 h-px bg-gray-200" />}
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${item.color}`} />
                  <span className="text-xs text-gray-400">{item.label}</span>
                  <span className="text-sm font-medium text-gray-700">{formatDateShort(item.date)}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Status Update */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
            Update Status
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
              <select
                value={statusUpdate.status}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                className={inputClass}
              >
                <option value="DRAFT">Draft</option>
                <option value="PENDING_REVIEW">Pending Review</option>
                <option value="APPROVED">Approved</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="REJECTED">Rejected</option>
                <option value="WAITLISTED">Waitlisted</option>
                <option value="WITHDRAWN">Withdrawn</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Review Notes <span className="text-gray-400 font-normal">(internal)</span>
              </label>
              <textarea
                value={statusUpdate.reviewNotes}
                onChange={(e) => setStatusUpdate({ ...statusUpdate, reviewNotes: e.target.value })}
                rows={3}
                className={`${inputClass} resize-none`}
                placeholder="Add internal notes..."
              />
            </div>

            <div className="md:col-span-2 border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={statusUpdate.sendEmail}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, sendEmail: e.target.checked })}
                  className="h-4 w-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label htmlFor="sendEmail" className="text-sm text-gray-700">
                  Send email notification to applicant
                </label>
              </div>

              {statusUpdate.sendEmail && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Email Message <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <textarea
                    value={statusUpdate.emailMessage}
                    onChange={(e) => setStatusUpdate({ ...statusUpdate, emailMessage: e.target.value })}
                    rows={3}
                    className={`${inputClass} resize-none`}
                    placeholder="Personalized message for the applicant..."
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    If left empty, review notes will be used.
                  </p>
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <button
                onClick={handleStatusUpdate}
                disabled={updating}
                className="px-5 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {updating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Updating...
                  </>
                ) : (
                  <>
                    Update Status
                    {statusUpdate.sendEmail && (
                      <span className="text-xs bg-cyan-500 px-2 py-0.5 rounded">+ Email</span>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Applicant Info + Course & Payment */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Applicant Information
            </h2>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <InfoField label="Name" value={`${application.givenNames || ""} ${application.familyName || ""}`.trim() || null} />
              <InfoField label="Gender" value={application.gender} />
              <InfoField label="Date of Birth" value={formatDateShort(application.dateOfBirth)} />
              <InfoField label="Nationality" value={application.nationality} />
              <InfoField label="Place of Birth" value={application.placeOfBirth} />
              <InfoField label="Email" value={application.email} />
              <InfoField label="WhatsApp" value={application.whatsappNumber} />
              <InfoField label="Passport" value={application.passportNumber} />
              <InfoField label="Passport Expiry" value={formatDateShort(application.passportExpiry)} />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Course & Payment
            </h2>
            <div className="space-y-3">
              <InfoField label="Course" value={application.course.title} />
              <InfoField label="University" value={application.course.university.name} />
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Payment Status</p>
                <StatusBadge status={application.paymentStatus} type="payment" />
              </div>
              {application.paymentReference && (
                <InfoField label="Payment Reference" value={application.paymentReference} />
              )}
              {application.paidAt && (
                <InfoField label="Paid At" value={formatDate(application.paidAt)} />
              )}
              {application.submittedAt && (
                <InfoField label="Submitted At" value={formatDate(application.submittedAt)} />
              )}
            </div>
          </div>
        </div>

        {/* Application Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <button
            className="flex items-center justify-between w-full text-left"
            onClick={() => setExpandedContent(!expandedContent)}
          >
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Application Content
              </h2>
              <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 font-medium ring-1 ring-inset ring-cyan-200">
                {contentCompleteness.filled}/{contentCompleteness.total} filled
              </span>
            </div>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expandedContent ? "rotate-180" : ""}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {expandedContent && (
            <div className="mt-5 space-y-5">
              {[
                { label: "Personal Statement", value: application.personalStatement },
                { label: "Study Plan", value: application.academicHistory },
                { label: "Work Experience", value: application.workExperience },
                { label: "References", value: application.references },
              ].map((section) => (
                <div key={section.label}>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">{section.label}</p>
                  {section.value ? (
                    <div className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border border-gray-100">
                      {section.value}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-300 italic">Not provided yet</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
            Documents ({application.documents.length})
          </h2>
          {application.documents.length === 0 ? (
            <p className="text-sm text-gray-300 italic">No documents uploaded yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {application.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50/50 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                    <p className="text-xs text-gray-400">{doc.documentType.replace(/_/g, " ")}</p>
                    {doc.rejectionReason && (
                      <p className="text-xs text-red-500 mt-0.5 truncate">↳ {doc.rejectionReason}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <StatusBadge
                      status={doc.isVerified ? "VERIFIED" : "UNVERIFIED"}
                      type="email"
                    />
                    <button
                      onClick={() => {
                        setSelectedDoc(doc);
                        setShowDocModal(true);
                        setDocAction(null);
                        setRejectionReason("");
                      }}
                      className="text-sm font-medium text-cyan-600 hover:text-cyan-800 transition-colors"
                    >
                      Preview
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Document Preview Modal */}
      {showDocModal && selectedDoc && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDocModal(false);
              setDocAction(null);
              setRejectionReason("");
            }
          }}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col"
            style={{ maxHeight: "90vh" }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex-1 min-w-0 mr-4">
                <p className="text-base font-semibold text-gray-900 truncate">{selectedDoc.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">{selectedDoc.documentType.replace(/_/g, " ")}</p>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <StatusBadge status={selectedDoc.isVerified ? "VERIFIED" : "UNVERIFIED"} type="email" />
                <button
                  onClick={() => { setShowDocModal(false); setDocAction(null); setRejectionReason(""); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className="flex-1 overflow-auto p-4 min-h-0">
              {selectedDoc.url ? (
                <iframe
                  src={selectedDoc.url}
                  className="w-full rounded border border-gray-200"
                  style={{ height: "420px" }}
                  title={selectedDoc.name}
                />
              ) : (
                <div className="flex items-center justify-center h-48 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-gray-400">No preview available</p>
                </div>
              )}
              {selectedDoc.rejectionReason && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs font-medium text-red-700">Previous Rejection Reason</p>
                  <p className="text-sm text-red-600 mt-0.5">{selectedDoc.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
              {docAction === "reject" ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Rejection Reason{" "}
                      <span className="text-gray-400 font-normal">
                        (optional — will be emailed to applicant)
                      </span>
                    </label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      rows={3}
                      className={`${inputClass} resize-none`}
                      placeholder="Explain why this document was rejected..."
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() =>
                        handleDocumentAction(selectedDoc.id, false, rejectionReason || undefined)
                      }
                      disabled={docActionLoading}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {docActionLoading && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      )}
                      Confirm Rejection
                    </button>
                    <button
                      onClick={() => { setDocAction(null); setRejectionReason(""); }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {!selectedDoc.isVerified ? (
                      <>
                        <button
                          onClick={() => handleDocumentAction(selectedDoc.id, true)}
                          disabled={docActionLoading}
                          className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          {docActionLoading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          )}
                          ✓ Verify
                        </button>
                        <button
                          onClick={() => setDocAction("reject")}
                          disabled={docActionLoading}
                          className="px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                        >
                          ✗ Reject
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setDocAction("reject")}
                        disabled={docActionLoading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                      >
                        Revoke Verification
                      </button>
                    )}
                  </div>
                  {selectedDoc.url && (
                    <a
                      href={selectedDoc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Open in new tab
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      <AdminModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        title="Send Status Notification"
        size="lg"
      >
        <div className="space-y-5">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <p className="text-sm text-blue-800">
              <span className="font-medium">To:</span> {application.user.firstName} {application.user.lastName} ({application.user.email})
            </p>
            <p className="text-sm text-blue-800 mt-1">
              <span className="font-medium">Status:</span> {application.status.replace("_", " ")}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Custom Message <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              rows={5}
              className={`${inputClass} resize-none`}
              placeholder="Add a personalized message for the applicant..."
            />
            <p className="text-xs text-gray-400 mt-1">
              This message will be included in the email alongside the status update.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowEmailModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSendEmail}
              disabled={sendingEmail}
              className="px-5 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {sendingEmail ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Sending...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send Email
                </>
              )}
            </button>
          </div>
        </div>
      </AdminModal>
    </AdminLayout>
  );
};

export default AdminApplicationDetails;
